import React, { useRef, useState, useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { useNavigate } from 'react-router-dom';
import { state } from '../store/global';
import API from '../lib/api';
import findMessage from '../utils/messages';

import Notification from '../components/common/Notification';
import Collapse from '../components/common/Collapse';
import DatePicker from '../components/common/DatePicker';
import Inputbox from '../components/common/Inputbox';
import Searchbar from '../components/common/Searchbar';
import Pagination from '../components/common/Pagination.jsx';

import FunnelIcon from '../assets/images/funnel.svg';
import TableIcon from '../assets/images/table.svg';
import SortIcon from '../assets/images/sort.svg';

export default function History() {
  const snap = useSnapshot(state);
  const navigate = useNavigate();

  const filterRef = useRef(null);
  const [notification, setNotification] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [transaction, setTransaction] = useState([]);
  const transactionHeader = [
    'Fecha',
    'Hora',
    'Tipo',
    'Movimiento',
    'Monto',
    'Estado',
  ];

  const movements = ['Recargas', 'Recompensas', 'Transferencias'];
  const stateTexts = ['Aprobado', 'Rechazado'];
  const [searchKeyword, setSearchKeyword] = useState('');
  const [types, setTypes] = useState([false, false, false]);
  const [states, setStates] = useState([false, false]);
  const [dates, setDates] = useState(['', '']);

  const onFilter = async () => {
    const query = [];

    if (searchKeyword) query.push(`keyword=${searchKeyword}`);

    const transactionTypes = [];
    if (types[0]) transactionTypes.push('ON RAMP');
    if (types[1]) transactionTypes.push('REWARD');
    if (types[2]) transactionTypes.push('OFF RAMP');
    if (transactionTypes.length)
      query.push(`types=${transactionTypes.join(',')}`);

    const transactionStates = [];
    if (states[0]) transactionStates.push('COMPLETED');
    if (states[1]) transactionStates.push('FAILED');
    if (transactionStates.length)
      query.push(`states=${transactionStates.join(',')}`);

    if (dates[0]) query.push(`startDate=${dates[0]}`);
    if (dates[1]) query.push(`endDate=${dates[1]}`);

    await fetchTransactions(query.join('&'));
    setIsOpen(false);
  };

  const fetchTransactions = async (query = '') => {
    await API.get(`transaction?${query}`)
      .then((res) => {
        setTransaction(res.data);
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || err?.message;
        console.log('msg: ', msg);
        if (msg == 'User not found') navigate('/login');
        else {
          setNotification({
            message: findMessage(msg),
            type: 'error',
          });
        }
      });
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getDescription = (item) => {
    if (item.type == 'ON RAMP') {
      if (item.payvalida) return 'De cuenta propia / Payvalida';
      else {
        if (
          item.currency == 'USD' ||
          item.currency == 'EUR' ||
          item.currency == 'MXN'
        ) {
          return item.virtual_account.source_deposit_instructions.bank_name;
        } else return 'Unknown bank';
      }
    } else {
      if (item.payvalida) return item.colombia_beneficiary.bank_name;
      else {
        if (
          item.destination.currency == 'USD' ||
          item.destination.currency == 'EUR' ||
          item.destination.currency == 'MXN'
        ) {
          return item.liquidation_address?.external_account[0].bank_name;
        } else return 'Unkonw bank';
      }
    }
  };

  // Pagination stateTexts
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = transaction.slice(startIndex, endIndex);

  return (
    <div className="relative px-10 py-8 space-y-8">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="py-4 space-y-[2px] border-b-1 border-b-[#E9EAEB]">
        <p className="text-xl font-semibold text-[#181D27]">
          Historial de movimientos
        </p>
        <p className="text-sm font-normal text-[#535862]">
          Seguí el estado de tus cuentas y movimientos en tiempo real
        </p>
      </div>

      <div className="bg-white rounded-[20px] px-4 py-6 space-y-8">
        <div className="space-x-8 flex items-center justify-between">
          <Searchbar
            customClass="py-3"
            placeholder="Buscar movimiento..."
            searchKeyword={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
          />

          <div className="relative" ref={filterRef}>
            <div className="px-4.5 py-3 space-x-3 flex items-center justify-center bg-[#F9FAFB] rounded-[10px] border-1 border-[#D5D7DA]">
              <p className="text-sm text-[#717680] leading-[22px] font-semibold">
                Filtros
              </p>
              <button
                onClick={() => setIsOpen((prev) => !prev)}
                className="w-6 h-6 cursor-pointer"
              >
                <img src={FunnelIcon} alt="Filter Icon" className="w-6 h-6" />
              </button>
            </div>

            {isOpen && (
              <div className="absolute z-50 right-12 top-[20px] w-60 bg-white border-1 border-[#F5F5F5] rounded-tl-2xl rounded-bl-2xl	rounded-br-2xl shadow-lg">
                <Collapse title="Movimientos">
                  {movements.map((item, idx) => (
                    <div
                      key={idx}
                      className="border-b border-[#F5F5F5] leading-[20px] px-4 py-2 flex items-center justify-between"
                    >
                      <span className="text-xs font-normal text-[#414651]">
                        {item}
                      </span>
                      <input
                        type="checkbox"
                        checked={types[idx]}
                        onChange={(e) =>
                          setTypes((prev) => {
                            prev[idx] = e.target.checked;
                            console.log('prev: ', prev);
                            return [...prev];
                          })
                        }
                        className="w-5 h-5"
                      />
                    </div>
                  ))}
                </Collapse>

                <Collapse title="Estados">
                  {stateTexts.map((item, idx) => (
                    <div
                      key={idx}
                      className="border-b border-[#F5F5F5] leading-[20px] px-4 py-2 flex items-center justify-between"
                    >
                      <span className="text-xs font-normal text-[#414651]">
                        {item}
                      </span>
                      <input
                        type="checkbox"
                        checked={states[idx]}
                        onChange={(e) =>
                          setStates((prev) => {
                            prev[idx] = e.target.checked;
                            console.log('prev: ', prev);
                            return [...prev];
                          })
                        }
                        className="w-5 h-5"
                      />
                    </div>
                  ))}
                </Collapse>

                <Collapse title="Fecha">
                  {['Desde', 'Hasta'].map((item, idx) => (
                    <DatePicker
                      key={idx}
                      title={item}
                      value={dates[idx]}
                      onChange={(e) =>
                        setDates((prev) => {
                          prev[idx] = e.target.value;
                          return [...prev];
                        })
                      }
                    />
                  ))}
                </Collapse>

                <div className="flex items-center justify-center py-2.5">
                  <button
                    onClick={() => {
                      setTypes([false, false, false]);
                      setStates([false, false]);
                      setDates(['', '']);
                      setIsOpen(false);
                    }}
                    className="px-4 py-2 cursor-pointer hover:bg-gray-100 rounded-[10px]"
                  >
                    <p className="text-sm text-[#717680] leading-[22px] font-medium inline">
                      Cancelar
                    </p>
                  </button>
                  <button
                    onClick={onFilter}
                    className="px-4 py-2 cursor-pointer hover:bg-gray-100 rounded-[10px]"
                  >
                    <p className="text-sm text-[#2A59C6] leading-[22px] font-medium underline inline">
                      Aplicar filtros
                    </p>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          {currentItems.length == 0 && (
            <div className="py-6 space-y-2 bg-[#FDFDFD] border-1 border-[#F5F5F5] rounded-[10px]">
              <div className="flex items-center justify-center">
                <div className="w-9 h-9 bg-[#E6EBF7] rounded-full flex items-center justify-center">
                  <img
                    src={TableIcon}
                    className="w-5 h-5 bg-[#E6EBF7] rounded-full flex items-center justify-center"
                  />
                </div>
              </div>
              <p className="text-center text-sm font-medium text-[#535862]">
                Todavía no tienes movimientos registrados
              </p>
              <p className="text-center text-sm font-normal text-[#717680]">
                Aquí verás todas tus recargas, transferencias, conversiones y
                recompensas una vez comiences a operar.
              </p>
            </div>
          )}

          {currentItems.length && (
            <div className="relative overflow-x-auto border-1 border-[#E1E1E1] sm:rounded-lg">
              <table className="w-full font-[poppins] text-sm text-left rtl:text-right text-[#535862]">
                <thead className="border-b-1 border-b-[#E1E1E1]">
                  <tr>
                    {transactionHeader.map((name, idx) => (
                      <th key={idx} scope="col" className="px-5 py-4.5">
                        <div className="flex items-center space-x-3">
                          <p className="font-normal">{name}</p>
                          {transactionHeader.length > idx + 1 && (
                            <img
                              src={SortIcon}
                              alt="sort icon"
                              className="w-6 h-6"
                            />
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((item, idx) => {
                    return (
                      <tr key={idx} className="border-b-1 border-b-[#E1E1E1]">
                        <td className="p-5 min-w-30">
                          <p className="text-sm text-[#252B37] leading-[20px]">
                            {item.created_at.slice(0, 10)}
                          </p>
                        </td>
                        <td className="p-5">
                          <p className="text-sm text-[#535862] leading-[20px]">
                            {item.created_at.slice(11, 19)}
                          </p>
                        </td>
                        <td className="p-5">
                          <p className="text-sm text-[#535862] font-semibold leading-[20px]">
                            {item.type}
                          </p>
                        </td>
                        <td className="p-2">
                          <div className="min-w-75">
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-[#535862]">ID de transacción:</p>
                              <p className="text-xs text-[#535862]">{item._id}</p>
                            </div>
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-[#535862]">Nombre del banco:</p>
                              <p className="text-xs text-[#535862]">{getDescription(item)}</p>
                            </div>
                          </div>
                        </td>
                        <td className="min-w-40 p-5">{`${item.amount < 0 ? '-' : '+'} $${Math.abs(
                          item.amount
                        ).toFixed(2)} ${item.currency}`}</td>

                        <td
                          className={`p-2 ${
                            item.status == 'CREATED'
                              ? ''
                              : item.status == 'FAILED'
                              ? 'text-[#F04438]'
                              : 'text-[#12B76A]'
                          }`}
                        >
                          {item.status}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Pagination
          total={transaction.length}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </div>
    </div>
  );
}
